/**
 * WholesaleSlabsViewer — buyer-side widget showing the bulk-discount
 * ladder and the live "next slab" hint based on current cart qty.
 *
 * Polls `/api/wholesale/effective/{pid}?qty=N` whenever qty changes
 * so the platform / per-seller master switches are respected. If the
 * product has no slabs OR wholesale is disabled, nothing renders.
 */
import React, { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { api } from "../lib/api";

export default function WholesaleSlabsViewer({ productId, slabs, qty, unitPrice }) {
  const [eff, setEff] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!productId) return;
    (async () => {
      try {
        const { data } = await api.get(`/wholesale/effective/${productId}`, {
          params: { qty: Math.max(1, qty || 1) },
        });
        if (alive) setEff(data);
      } catch { /* widget is optional */ }
    })();
    return () => { alive = false; };
  }, [productId, qty]);

  // Nothing to render if seller hasn't defined slabs.
  if (!Array.isArray(slabs) || slabs.length === 0) return null;

  // Wholesale was disabled at platform or seller level → keep the widget
  // silent so the buyer isn't teased by a deal they can't use.
  if (eff && !eff.eligible) return null;

  const currentSlab = eff?.slab;
  const nextSlab = eff?.next_slab;
  const savedPerLine = currentSlab && unitPrice
    ? (unitPrice * (qty || 1) * (currentSlab.discount_pct / 100)).toFixed(2)
    : null;

  return (
    <div className="mb-5 p-4 rounded-xl border border-[#D4FF00]/30 bg-[#D4FF00]/[0.04]"
      data-testid="product-wholesale-block">
      <div className="flex items-center gap-2 mb-3">
        <Tag size={14} className="text-[#D4FF00]"/>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#D4FF00]">
          Bulk pricing
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-2">
        {slabs.map((s) => {
          const isActive = currentSlab && s.min_qty === currentSlab.min_qty;
          return (
            <div key={s.min_qty}
              data-testid={`wholesale-slab-${s.min_qty}`}
              className={`rounded-lg p-2 text-center border transition ${
                isActive
                  ? "border-[#D4FF00] bg-[#D4FF00]/15"
                  : "border-[#3D2845] bg-[#261534]"
              }`}>
              <div className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold">
                Buy {s.min_qty}+
              </div>
              <div className={`font-display font-black text-lg ${isActive ? "text-[#D4FF00]" : "text-[#FAFAFA]"}`}>
                −{s.discount_pct}%
              </div>
            </div>
          );
        })}
      </div>

      {currentSlab && savedPerLine && (
        <div className="text-xs text-[#D4FF00] font-bold" data-testid="wholesale-saved-line">
          ✓ You save ${savedPerLine} at qty {qty} ({currentSlab.discount_pct}% off)
        </div>
      )}
      {!currentSlab && nextSlab && (
        <div className="text-xs text-[#A1A1AA]" data-testid="wholesale-next-hint">
          Add {nextSlab.min_qty - (qty || 1)} more to unlock <span className="text-[#D4FF00] font-bold">−{nextSlab.discount_pct}%</span>.
        </div>
      )}
    </div>
  );
}
